import SplashScreen from "../components/base/SplashScreen";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import RoleProtectedRoute from "./RoleProtectedRoute";
import PtarAppProtectedRoute from "./PtarAppProtectedRoute";
import SgAccessProtectedRoute from "./SgAccessProtectedRoute";
import { ADMIN_RESELLER_ROLE_ID } from "../hooks/useIsReseller";
import { ADMIN_APP_RESTRICTED_ROLE_IDS } from "./adminAppAccess";
import AdminIndexRedirect from "./AdminIndexRedirect";

// Layouts (Home, Lite, Admin, Oasis)
import AdminLayout from "../Layouts/admin/AdminLayout";
import OasisLayout from "../Layouts/oasis/OasisLayout";
import LiteLayout from "../Layouts/lite/LiteLayout";
import SGLayout from "../Layouts/sg/SGLayout";
import ReportLayout from "../Layouts/reports/ReportLayout";
import FacturacionLayout from "../Layouts/facturacion/FacturacionLayout";
import CobranzaLayout from "../Layouts/cobranza/CobranzaLayout";
import ResellerAdminLayout from "../Layouts/reseller-admin/ResellerAdminLayout";
import PapeleraLayout from "../Layouts/papelera/PapeleraLayout";
import PapeleraSection from "../Layouts/papelera/PapeleraSection";
import PapeleraIndexRedirect from "../Layouts/papelera/PapeleraIndexRedirect";
import EntregaServicioLayout from "../Layouts/entrega-servicio/EntregaServicioLayout";
import DashboardsLayout from "../Layouts/dashboards/DashboardsLayout";
import AiLayout from "../Layouts/ai/AiLayout";
import AiChat from "../Layouts/ai/components/AiChat";
import SupportLayout from "../Layouts/support/SupportLayout";
import SupportChatPage from "../pages/support/SupportChatPage";
import XmlLayout from "../Layouts/xml/XmlLayout";
import IntegradoresLayout from "../Layouts/integradores/IntegradoresLayout";
import RespaldosLayout from "../Layouts/respaldos/RespaldosLayout";
import RespaldosList from "../pages/respaldos/list/RespaldosList";
import JobDetail from "../pages/respaldos/detail/JobDetail";
import TruckYardLayout from "../Layouts/patio-maniobras/TruckYardLayout";
import InspectionsLayout from "../Layouts/inspeccion/InspectionsLayout";
import { serviceDeskRoute } from "../Layouts/service-desk/routes";
import InspectionHistorySection from "../pages/inspecciones/inspectionHistory/inspectionHistorySection.tsx";
import YardsSection from "../pages/patios-maniobras/yardsSection/page";
import PersonalSection from "../pages/patios-maniobras/personalSection/page";
import AccessSection from "../pages/patios-maniobras/accessSection/page";
import AccessControlSection from "../pages/patios-maniobras/accessControlSection/page";
import AccessRecordsSection from "../pages/patios-maniobras/accessRecordsSection/page";
import RelaySection from "../pages/patios-maniobras/relaySection/page";
import TruckYardRoleRoute from "../pages/patios-maniobras/components/TruckYardRoleRoute";
import TruckYardIndexRedirect from "../pages/patios-maniobras/components/TruckYardIndexRedirect";
import VehiclesSection from "../pages/patios-maniobras/vehiclesSection/page";
import TruckYardGroupsSection from "../pages/patios-maniobras/groupsSection/page";
import AccessCardsSection from "../pages/patios-maniobras/accessCardsSection/page";

// POS Layouts
import POSCashierLayout from "../Layouts/pos/cashier/POSCashierLayout";
import POSProductsLayout from "../Layouts/pos/products/POSProductsLayout";
import POSInventoryLayout from "../Layouts/pos/inventory/POSInventoryLayout";
import POSCouponsLayout from "../Layouts/pos/coupons/POSCouponsLayout";
import POSOrdersLayout from "../Layouts/pos/orders/POSOrdersLayout";
import POSEcommerceLayout from "../Layouts/pos/ecommerce/POSEcommerceLayout";

// POS Pages
import TerminalSection from "../pages/pos/cashier/TerminalSection";
import QuickSaleSection from "../pages/pos/cashier/QuickSaleSection";
import CartSection from "../pages/pos/cashier/CartSection";
import CheckoutSection from "../pages/pos/cashier/CheckoutSection";
import ReceiptsSection from "../pages/pos/cashier/ReceiptsSection";
import RegisterSection from "../pages/pos/cashier/RegisterSection";
import ProductListSection from "../pages/pos/products/ProductListSection";
import CreateProductSection from "../pages/pos/products/CreateProductSection";
import CategoriesSection from "../pages/pos/products/CategoriesSection";
import VariantsSection from "../pages/pos/products/VariantsSection";
import PricingSection from "../pages/pos/products/PricingSection";
import ImportSection from "../pages/pos/products/ImportSection";
import ExportSection from "../pages/pos/products/ExportSection";
import StockSection from "../pages/pos/inventory/StockSection";
import LowStockSection from "../pages/pos/inventory/LowStockSection";
import AdjustmentsSection from "../pages/pos/inventory/AdjustmentsSection";
import TransfersSection from "../pages/pos/inventory/TransfersSection";
import MovementsSection from "../pages/pos/inventory/MovementsSection";
import LocationsSection from "../pages/pos/inventory/LocationsSection";
import SuppliersSection from "../pages/pos/inventory/SuppliersSection";
import CouponListSection from "../pages/pos/coupons/CouponListSection";
import CreateCouponSection from "../pages/pos/coupons/CreateCouponSection";
import PromotionsSection from "../pages/pos/coupons/PromotionsSection";
import DiscountsSection from "../pages/pos/coupons/DiscountsSection";
import AnalyticsSection from "../pages/pos/coupons/AnalyticsSection";
import UsageHistorySection from "../pages/pos/coupons/UsageHistorySection";
import SalesSection from "../pages/pos/orders/SalesSection";
import POSOrdersSection from "../pages/pos/orders/POSOrdersSection";
import OnlineOrdersSection from "../pages/pos/orders/OnlineOrdersSection";
import RefundsSection from "../pages/pos/orders/RefundsSection";
import ReturnsSection from "../pages/pos/orders/ReturnsSection";
import OrdersReceiptsSection from "../pages/pos/orders/ReceiptsSection";
import ReportsSection from "../pages/pos/orders/ReportsSection";
import EcommerceDashboard from "../pages/pos/ecommerce/EcommerceDashboard";
import SystemHealthOverview from "../Layouts/entrega-servicio/components/SystemHealthOverview";
import EntregaServicioPlaceholder from "../Layouts/entrega-servicio/components/EntregaServicioPlaceholder";
import HomeView from "../pages/home/HomeView";
import NotFound from "../pages/NotFound";
import ProfilePage from "../pages/profile/ProfilePage";
import SettingsPage from "../pages/settings/SettingsPage";
import ProfileLayout from "../Layouts/profile/ProfileLayout";

// Public Pages
import PublicTrackingLinkPage from "../pages/public/tracking/PublicTrackingLinkPage";
import CameraSessionPage from "../pages/inspecciones/camera/CameraSessionPage";
import ConfirmSessionResetPage from "../pages/public/confirm-session-reset/ConfirmSessionResetPage";
import ResetPasswordPage from "../pages/public/reset-password/ResetPasswordPage";
import TrackingReplayRecordingPage from "../pages/tracking-replay/TrackingReplayRecordingPage";

// Sections Admin
import UnitsSection from "../Layouts/admin/components/sections/UnitsSection";
import DevicesSection from "../Layouts/admin/components/sections/devices/DevicesSection";
import TrailersSection from "../Layouts/admin/components/sections/trailers/TrailersSection";
import TrucksSection from "../Layouts/admin/components/sections/TrucksSection";
import GeofencesSection from "../Layouts/admin/components/sections/geofences/GeofencesSection";
import AlertsSection from "../Layouts/admin/components/sections/AlertsSection";
import NotificationsSection from "../Layouts/admin/components/sections/notifications/NotificationsSection";
import DriversSection from "../Layouts/admin/components/sections/DriversSection";
import FleetCostsSection from "../Layouts/admin/components/sections/fleetCosts/FleetCostsSection";
import PoliciesSection from "../Layouts/admin/components/sections/PoliciesSection";
import AvisosSection from "../Layouts/admin/components/sections/avisos";
import SessionsSection from "../Layouts/admin/components/sections/sessions";
import PointOfInterestsSection from "../Layouts/admin/components/sections/PointOfInterestsSection";
import TrackingLinksSection from "../Layouts/admin/components/sections/trackingLinks/TrackingLinksSection";
import { TrackingReplaysSection } from "../Layouts/admin/components/sections/trackingReplays";
import GroupsSection from "../Layouts/admin/components/sections/groups";
import LicensesSection from "../Layouts/reseller-admin/components/sections/licenses/LicensesSection";
import { ModelsSection } from "../Layouts/reseller-admin/components/sections/models";
import { IntegratorsSection } from "../Layouts/reseller-admin/components/sections/integrators";
import { ProvidersSection } from "../Layouts/reseller-admin/components/sections/providers";
// import MaponDevicesSection from "../Layouts/reseller-admin/components/sections/mapon-devices/MaponDevicesSection";
// import MaponUsersSection from "../Layouts/reseller-admin/components/sections/mapon-users/MaponUsersSection";
// import WialonDevicesSection from "../Layouts/reseller-admin/components/sections/wialon-devices/WialonDevicesSection";
// import WialonUsersSection from "../Layouts/reseller-admin/components/sections/wialon-users/WialonUsersSection";
import UsersSection from "../pages/facturacion/users/UsersSection";
import TripsSection from "../Layouts/admin/components/sections/trips/TripsSection";
import RoutesSection from "../Layouts/admin/components/sections/RoutesSection";

// Section Lite
import MapView from "../pages/home/components/MapView";

// Section Oasis
import YardsList from "../pages/oasis/yards-section/list/YardsList";
import YardsForm from "../pages/oasis/yards-section/form/YardsForm";
import AccessList from "../pages/oasis/access-section/AccessList";

// Section SG
import UsersList from "../pages/sg/users-section/list/UsersList";
import UsersForm from "../pages/sg/users-section/form/UsersForm";
import DevicesList from "../pages/sg/devices-section/list/DevicesList";
import DevicesForm from "../pages/sg/devices-section/form/DevicesForm";
import Plataformas from "../pages/sg/plataformas-section/Plataformas";
import Informes from "../pages/sg/informes-section/Informes";
import Cuadre from "../pages/sg/cuadre-section/Cuadre";
import HistorySection from "../pages/reports/history-section/HistorySection";
import EventsSection from "../pages/reports/events-section/EventsSection";
import SpeedSection from "../pages/reports/speed-section/SpeedSection";
import TemperatureSection from "../pages/reports/temperature-section/TemperatureSection";
import BehaviorSection from "../pages/reports/behavior-section/BehaviorSection";
import AlertsReportSection from "../pages/reports/alerts-section/AlertsSection";
import VoltageSection from "../pages/reports/voltage-section/VoltageSection";
import GpsQualitySection from "../pages/reports/gps-quality-section/GpsQualitySection";
import DrivingSection from "../pages/reports/driving-section/DrivingSection";
import DrivingHistorySection from "../pages/reports/driving-history-section/DrivingHistorySection";
import MileageSection from "../pages/reports/mileage-section/MileageSection";
import MonthlySection from "../pages/reports/monthly-section/MonthlySection";
import TrailerSection from "../pages/reports/trailer-section/TrailerSection";
import GeofecensesSection from "../pages/reports/geofences-section/GeofecensesSection";
import RoutesReportSection from "../pages/reports/routes-section/RoutesReportSection";
import OdometerSection from "../pages/reports/odometer-section/OdometerSection";
import StopSection from "../pages/reports/stop-section/StopSection";
import CommandsSection from "../pages/reports/commands-section/CommandsSection";
import QuickReportsPage from "../pages/quick-reports/QuickReportsPage";
import ScheduledReportsPage from "../pages/quick-reports/ScheduledReportsPage";

// Sections Facturacion
import ResumenSection from "../pages/facturacion/resumen/ResumenSection";

// Sections Cobranza
import CobranzaResumenSection from "../pages/cobranza/resumen/ResumenSection";
import CompaniesSection from "../pages/cobranza/companies/CompaniesSection";

// Sections Dashboards
import CobranzaDashboard from "../pages/dashboards/cobranza/CobranzaDashboard";
import LogisticaDashboard from "../pages/dashboards/logistica/LogisticaDashboard";
import V5Dashboard from "../pages/dashboards/v5/V5Dashboard";
import EntregaServicioDashboardLayout from "../Layouts/entrega-servicio-dashboard/EntregaServicioDashboardLayout";
import EntregaServicioDashboard from "../pages/entrega-servicio/EntregaServicioDashboard";
import MaponDashboard from "../pages/dashboards/mapon/MaponDashboard";
import WialonDashboard from "../pages/dashboards/wialon/WialonDashboard";
import PagosSection from "../pages/cobranza/pagos/PagosSection";
import AdeudosSection from "../pages/cobranza/adeudos/AdeudosSection";
import CobranzaHistorialSection from "../pages/cobranza/historial/HistorialSection";
import MetodosPagoSection from "../pages/facturacion/metodos-pago/MetodosPagoSection";
import InformacionSection from "../pages/facturacion/informacion/InformacionSection";
import HistorialSection from "../pages/facturacion/historial/HistorialSection";
import XmlMonitor from "@/Layouts/xml/components/XmlMonitor";
import SoporteLayout from "../pages/public/soporte/SoporteLayout";
import SoporteNuevo from "../pages/public/soporte/SoporteNuevo";
import SoporteSeguimiento from "../pages/public/soporte/SoporteSeguimiento";
import MisTicketsPage from "../pages/soporte/MisTicketsPage";

// Quién no puede entrar a `/admin` está en ./adminAppAccess (con el porqué):
// lo leen este guard y los enlaces que apuntan a `/admin/...`.

const routes: RouteObject[] = [
  {
    path: "/camera/:sessionId",
    element: <CameraSessionPage />,
  },
  {
    path: "/splash",
    element: <SplashScreen />,
  },
  {
    path: "/",
    element: <HomeView />,
  },
  {
    path: "/trackingLinks/public",
    element: <PublicTrackingLinkPage />,
  },
  {
    path: "/confirm-session-reset",
    element: <ConfirmSessionResetPage />,
  },
  {
    path: "/reset-password",
    element: <ResetPasswordPage />,
  },
  {
    path: "/soporte",
    element: <SoporteLayout />,
    children: [
      { index: true, element: <SoporteNuevo /> },
      { path: "seguimiento", element: <SoporteSeguimiento /> },
    ],
  },
  {
    path: "/mis-tickets",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [{ index: true, element: <MisTicketsPage /> }],
  },
  {
    path: "/tracking-replay/recording",
    element: <TrackingReplayRecordingPage />,
  },
  {
    path: "/profile",
    element: <ProfileLayout />,
    children: [
      {
        index: true,
        element: <ProfilePage />,
      },
    ],
  },
  {
    path: "/ajustes",
    element: <ProfileLayout appId="ajustes" />,
    children: [
      {
        index: true,
        element: <SettingsPage />,
      },
    ],
  },
  {
    path: "/map",
    element: <LiteLayout />,
    children: [
      {
        index: true,
        element: <MapView />,
      },
    ],
  },
  {
    path: "/admin",
    element: (
      <RoleProtectedRoute
        restrictedRoleIds={ADMIN_APP_RESTRICTED_ROLE_IDS}
        redirectTo="/map"
      />
    ),
    children: [
      {
        path: "",
        element: <AdminLayout />,
        children: [
          {
            index: true,
            element: <AdminIndexRedirect />,
          },
          {
            path: "units",
            element: <UnitsSection />,
          },
          {
            path: "devices",
            element: <DevicesSection />,
          },
          {
            path: "trailers",
            element: <TrailersSection />,
          },
          {
            path: "trucks",
            element: <TrucksSection />,
          },
          {
            path: "geofences",
            element: <GeofencesSection key="geofences" />,
          },
          {
            path: "alerts",
            element: <AlertsSection />,
          },
          {
            path: "notifications",
            element: <NotificationsSection />,
          },
          {
            path: "drivers",
            element: <DriversSection />,
          },
          {
            path: "fleet-costs",
            element: <FleetCostsSection />,
          },
          {
            path: "policies",
            element: <PoliciesSection />,
          },
          {
            path: "avisos",
            element: <AvisosSection />,
          },
          {
            path: "sessions",
            element: <SessionsSection />,
          },
          {
            path: "routes",
            element: <RoutesSection />,
          },
          {
            path: "pointOfInterests",
            element: <PointOfInterestsSection />,
          },
          {
            path: "tracking-links",
            element: <TrackingLinksSection />,
          },
          {
            path: "groups",
            element: <GroupsSection />,
          },
          {
            path: "tracking-replays",
            element: <TrackingReplaysSection />,
          },
          {
            path: "usuarios",
            element: <UsersSection />,
          },
          {
            path: "trips",
            element: <TripsSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/papelera",
    element: <PapeleraLayout mode="fleet" />,
    children: [
      {
        index: true,
        element: <PapeleraIndexRedirect />,
      },
      {
        path: ":entity",
        element: <PapeleraSection />,
      },
    ],
  },
  {
    path: "/papelera-interno",
    element: <RoleProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <PapeleraLayout mode="internal" />,
        children: [
          {
            index: true,
            element: <PapeleraIndexRedirect />,
          },
          {
            path: ":entity",
            element: <PapeleraSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/oasis",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <OasisLayout />,
        children: [
          {
            path: "yards",
            element: <YardsList />,
          },
          {
            path: "yards/form",
            element: <YardsForm />,
          },
          {
            path: "security/access",
            element: <AccessList />,
          },
        ],
      },
    ],
  },
  {
    path: "/sg",
    element: <SgAccessProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <SGLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="cuadre" replace />,
          },
          {
            path: "cuadre",
            element: <Cuadre />,
          },
          {
            path: "users",
            element: <UsersList />,
          },
          {
            path: "users/form",
            element: <UsersForm />,
          },
          {
            path: "users/edit/:id",
            element: <UsersForm />,
          },
          {
            path: "devices",
            element: <DevicesList />,
          },
          {
            path: "devices/form",
            element: <DevicesForm />,
          },
          {
            path: "devices/edit/:id",
            element: <DevicesForm />,
          },
          {
            path: "plataformas",
            element: <Plataformas />,
          },
          {
            path: "informes",
            element: <Informes />,
          },
          // El barrido dejó de ser un área: es un informe más (links viejos).
          {
            path: "barrido",
            element: <Navigate to="/sg/informes" replace />,
          },
        ],
      },
    ],
  },
  {
    path: "/reports",
    element: <ReportLayout />,
    children: [
      {
        path: "history",
        element: <HistorySection />,
      },
      {
        path: "events",
        element: <EventsSection />,
      },
      {
        path: "speed",
        element: <SpeedSection />,
      },
      {
        path: "temperature",
        element: <TemperatureSection />,
      },
      {
        path: "behavior",
        element: <BehaviorSection />,
      },
      {
        path: "alerts",
        element: <AlertsReportSection />,
      },
      {
        path: "voltage",
        element: <VoltageSection />,
      },
      {
        path: "gps-quality",
        element: <GpsQualitySection />,
      },
      {
        path: "driving",
        element: <DrivingSection />,
      },
      {
        path: "workday",
        element: <DrivingHistorySection />,
      },
      {
        path: "mileage",
        element: <MileageSection />,
      },
      {
        path: "monthly",
        element: <MonthlySection />,
      },
      {
        path: "geofences",
        element: <GeofecensesSection />,
      },
      {
        path: "trailer",
        element: <TrailerSection />,
      },
      {
        path: "routes",
        element: <RoutesReportSection />,
      },
      {
        path: "odometer",
        element: <OdometerSection />,
      },
      {
        path: "stop",
        element: <StopSection />,
      },
      {
        path: "commands",
        element: <CommandsSection />,
      },
      {
        path: "quick",
        element: <QuickReportsPage />,
      },
      {
        path: "scheduled",
        element: <ScheduledReportsPage />,
      },
    ],
  },
  {
    path: "/entrega-servicio",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <EntregaServicioLayout />,
        children: [
          {
            index: true,
            element: <EntregaServicioPlaceholder />,
          },
        ],
      },
    ],
  },
  {
    path: "/entrega-servicio-dashboard",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <EntregaServicioDashboardLayout />,
        children: [
          {
            index: true,
            element: <EntregaServicioDashboard />,
          },
        ],
      },
    ],
  },
  {
    path: "/facturacion",
    element: <FacturacionLayout />,
    children: [
      {
        index: true,
        element: <ResumenSection />,
      },
      {
        path: "resumen",
        element: <ResumenSection />,
      },
      {
        path: "usuarios",
        element: <UsersSection />,
      },
      {
        path: "dispositivos",
        element: <DevicesSection />,
      },
      {
        path: "metodos-pago",
        element: <MetodosPagoSection />,
      },
      {
        path: "informacion",
        element: <InformacionSection />,
      },
      {
        path: "historial",
        element: <HistorialSection />,
      },
    ],
  },
  {
    path: "/cobranza",
    element: <RoleProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <CobranzaLayout />,
        children: [
          {
            index: true,
            element: <CobranzaResumenSection />,
          },
          {
            path: "resumen",
            element: <CobranzaResumenSection />,
          },
          {
            path: "companies",
            element: <CompaniesSection />,
          },
          {
            path: "usuarios",
            element: <UsersSection />,
          },
          {
            path: "dispositivos",
            element: <DevicesSection />,
          },
          {
            path: "pagos",
            element: <PagosSection />,
          },
          {
            path: "adeudos",
            element: <AdeudosSection />,
          },
          {
            path: "historial",
            element: <CobranzaHistorialSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/reseller-admin",
    element: <RoleProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <ResellerAdminLayout />,
        children: [
          {
            index: true,
            element: <CompaniesSection />,
          },
          {
            path: "companies",
            element: <CompaniesSection />,
          },
          {
            path: "devices",
            element: <DevicesSection />,
          },
          {
            path: "device-brands",
            element: <DevicesSection />,
          },
          {
            path: "users",
            element: <UsersSection />,
          },
          {
            path: "user-roles",
            element: <UsersSection />,
          },
          {
            path: "reseller-users",
            element: <UsersSection />,
          },
          {
            path: "reseller-user-roles",
            element: <UsersSection />,
          },
          {
            path: "licenses",
            element: <LicensesSection />,
          },
          {
            path: "models",
            element: <ModelsSection />,
          },
          {
            path: "integrators",
            element: <IntegratorsSection />,
          },
          {
            path: "providers",
            element: <ProvidersSection />,
          },
          // {
          //   path: "mapon-devices",
          //   element: <MaponDevicesSection />,
          // },
          // {
          //   path: "mapon-users",
          //   element: <MaponUsersSection />,
          // },
          // {
          //   path: "wialon-devices",
          //   element: <WialonDevicesSection />,
          // },
          // {
          //   path: "wialon-users",
          //   element: <WialonUsersSection />,
          // },
          {
            path: "avisos",
            element: <AvisosSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/dashboards",
    element: <SgAccessProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <DashboardsLayout />,
        children: [
          {
            index: true,
            element: <SystemHealthOverview />,
          },
          {
            path: "entrega-servicio",
            element: <SystemHealthOverview />,
          },
          {
            path: "cobranza",
            element: <CobranzaDashboard />,
          },
          {
            path: "logistica",
            element: <LogisticaDashboard />,
          },
          {
            path: "v5",
            element: <V5Dashboard />,
          },
          {
            path: "mapon",
            element: <MaponDashboard />,
          },
          {
            path: "wialon",
            element: <WialonDashboard />,
          },
        ],
      },
    ],
  },
  {
    path: "/ai",
    element: <RoleProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <AiLayout />,
        children: [
          {
            index: true,
            element: <AiChat />,
          },
        ],
      },
    ],
  },
  {
    path: "/support",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <SupportLayout />,
        children: [
          {
            index: true,
            element: <SupportChatPage />,
          },
        ],
      },
    ],
  },
  {
    path: "/xml",
    element: <RoleProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <XmlLayout />,
        children: [
          {
            index: true,
            element: <XmlMonitor />,
          },
        ],
      },
    ],
  },
  {
    path: "/integradores",
    element: <RoleProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <IntegradoresLayout mode="internal" />,
      },
    ],
  },
  {
    path: "/flota/integradores",
    element: <IntegradoresLayout mode="fleet" />,
  },
  {
    path: "/respaldos",
    element: <RoleProtectedRoute allowedRoleIds={[ADMIN_RESELLER_ROLE_ID]} />,
    children: [
      {
        path: "",
        element: <RespaldosLayout />,
        children: [
          {
            index: true,
            element: <RespaldosList />,
          },
          {
            path: ":jobId",
            element: <JobDetail />,
          },
        ],
      },
    ],
  },
  {
    path: "/truck-yard",
    element: <PtarAppProtectedRoute appId="truck-yard" />,
    children: [
      {
        element: <TruckYardLayout />,
        children: [
          {
            index: true,
            element: <TruckYardIndexRedirect />,
          },
          {
            path: "yards",
            element: <YardsSection />,
          },
          {
            path: "docks",
            element: <div className="p-6">Andenes - Coming soon...</div>,
          },
          {
            path: "personal",
            element: <PersonalSection />,
          },
          {
            path: "access",
            element: <AccessSection />,
          },
          {
            path: "access-control",
            element: (
              <TruckYardRoleRoute section="access-control">
                <AccessControlSection />
              </TruckYardRoleRoute>
            ),
          },
          {
            path: "access-records",
            element: (
              <TruckYardRoleRoute section="access-records">
                <AccessRecordsSection />
              </TruckYardRoleRoute>
            ),
          },
          {
            path: "relays",
            element: (
              <TruckYardRoleRoute section="relays">
                <RelaySection />
              </TruckYardRoleRoute>
            ),
          },
          {
            path: "vehicles",
            element: <VehiclesSection />,
          },
          {
            path: "groups",
            element: <TruckYardGroupsSection />,
          },
          {
            path: "access-cards",
            element: (
              <TruckYardRoleRoute section="access-cards">
                <AccessCardsSection />
              </TruckYardRoleRoute>
            ),
          },
          {
            path: "trucks",
            element: <div className="p-6">Camiones - Coming soon...</div>,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/cashier",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSCashierLayout />,
        children: [
          {
            index: true,
            element: <TerminalSection />,
          },
          {
            path: "terminal",
            element: <TerminalSection />,
          },
          {
            path: "quick-sale",
            element: <QuickSaleSection />,
          },
          {
            path: "cart",
            element: <CartSection />,
          },
          {
            path: "checkout",
            element: <CheckoutSection />,
          },
          {
            path: "receipts",
            element: <ReceiptsSection />,
          },
          {
            path: "register",
            element: <RegisterSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/products",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSProductsLayout />,
        children: [
          {
            index: true,
            element: <ProductListSection />,
          },
          {
            path: "list",
            element: <ProductListSection />,
          },
          {
            path: "create",
            element: <CreateProductSection />,
          },
          {
            path: "categories",
            element: <CategoriesSection />,
          },
          {
            path: "variants",
            element: <VariantsSection />,
          },
          {
            path: "pricing",
            element: <PricingSection />,
          },
          {
            path: "import",
            element: <ImportSection />,
          },
          {
            path: "export",
            element: <ExportSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/inventory",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSInventoryLayout />,
        children: [
          {
            index: true,
            element: <StockSection />,
          },
          {
            path: "stock",
            element: <StockSection />,
          },
          {
            path: "low-stock",
            element: <LowStockSection />,
          },
          {
            path: "adjustments",
            element: <AdjustmentsSection />,
          },
          {
            path: "transfers",
            element: <TransfersSection />,
          },
          {
            path: "movements",
            element: <MovementsSection />,
          },
          {
            path: "locations",
            element: <LocationsSection />,
          },
          {
            path: "suppliers",
            element: <SuppliersSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/coupons",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSCouponsLayout />,
        children: [
          {
            index: true,
            element: <CouponListSection />,
          },
          {
            path: "list",
            element: <CouponListSection />,
          },
          {
            path: "create",
            element: <CreateCouponSection />,
          },
          {
            path: "promotions",
            element: <PromotionsSection />,
          },
          {
            path: "discounts",
            element: <DiscountsSection />,
          },
          {
            path: "analytics",
            element: <AnalyticsSection />,
          },
          {
            path: "usage",
            element: <UsageHistorySection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/orders",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSOrdersLayout />,
        children: [
          {
            index: true,
            element: <SalesSection />,
          },
          {
            path: "sales",
            element: <SalesSection />,
          },
          {
            path: "pos-orders",
            element: <POSOrdersSection />,
          },
          {
            path: "online-orders",
            element: <OnlineOrdersSection />,
          },
          {
            path: "refunds",
            element: <RefundsSection />,
          },
          {
            path: "returns",
            element: <ReturnsSection />,
          },
          {
            path: "receipts",
            element: <OrdersReceiptsSection />,
          },
          {
            path: "reports",
            element: <ReportsSection />,
          },
        ],
      },
    ],
  },
  {
    path: "/pos/ecommerce",
    element: <RoleProtectedRoute restrictedRoles={["web_services"]} />,
    children: [
      {
        path: "",
        element: <POSEcommerceLayout />,
        children: [
          {
            index: true,
            element: <EcommerceDashboard />,
          },
          {
            path: "dashboard",
            element: <EcommerceDashboard />,
          },
          {
            path: "storefront",
            element: <EcommerceDashboard />,
          },
          {
            path: "pages",
            element: <EcommerceDashboard />,
          },
          {
            path: "theme",
            element: <EcommerceDashboard />,
          },
          {
            path: "navigation",
            element: <EcommerceDashboard />,
          },
          {
            path: "shipping",
            element: <EcommerceDashboard />,
          },
          {
            path: "payments",
            element: <EcommerceDashboard />,
          },
          {
            path: "settings",
            element: <EcommerceDashboard />,
          },
        ],
      },
    ],
  },
  {
    path: "/inspecciones",
    element: <PtarAppProtectedRoute appId="inspecciones" />,
    children: [
      {
        element: <InspectionsLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/inspecciones/historial" replace />,
          },
          {
            path: "historial",
            element: <InspectionHistorySection />,
          },
        ],
      },
    ],
  },
  serviceDeskRoute,
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
